import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInventory } from '../../contexts/InventoryContext';
import supabase from '../../supabase/supabase';
import SafeIcon from '../../common/SafeIcon';
import toast from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiSave, FiCamera, FiImage, FiTrash2, FiPlus } = FiIcons;

const AddProductModal = ({ isOpen, onClose }) => {
  const { addProduct, pallets } = useInventory();
  const [formData, setFormData] = useState({
    manufacturer: '',
    model: '',
    name: '',
    description: '',
    upc: '',
    localSku: '',
    localPrice: '',
    images: [],
    palletId: ''
  });

  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    return () => { if (cameraStream) cameraStream.getTracks().forEach(t => t.stop()); };
  }, [cameraStream]);

  useEffect(() => {
    if (showCamera && cameraStream && videoRef.current) {
      videoRef.current.srcObject = cameraStream;
      videoRef.current.play();
    }
  }, [showCamera, cameraStream]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
        .catch(() => navigator.mediaDevices.getUserMedia({ video: true }));
      setCameraStream(stream);
      setShowCamera(true);
    } catch (err) {
      toast.error("Camera access failed");
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d').drawImage(video, 0, 0);
      canvas.toBlob(async (blob) => {
        if (blob) await uploadImage(blob);
      }, 'image/jpeg', 0.8);
    }
  };

  const uploadImage = async (fileOrBlob) => {
    setUploadingImage(true);
    try {
      const fileName = `temp/${Date.now()}.jpg`;
      const { error } = await supabase.storage.from('product-images').upload(fileName, fileOrBlob);
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from('product-images').getPublicUrl(fileName);
      setFormData(prev => ({ ...prev, images: [...prev.images, publicUrl] }));
      toast.success('Photo added');
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const success = await addProduct({
      ...formData,
      localPrice: parseFloat(formData.localPrice) || 0
    });
    if (success) {
      onClose();
      setFormData({ manufacturer: '', model: '', name: '', description: '', upc: '', localSku: '', localPrice: '', images: [], palletId: '' });
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white rounded-xl shadow-xl max-w-2xl w-full my-8">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold">Add New Product</h2>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><SafeIcon icon={FiX} className="h-6 w-6" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">Product Photos</label>
                  {!showCamera && (
                    <button type="button" onClick={startCamera} className="text-sm text-blue-600 font-medium flex items-center">
                      <SafeIcon icon={FiCamera} className="mr-1 h-4 w-4" /> Take Photo
                    </button>
                  )}
                </div>

                {showCamera && (
                  <div className="relative bg-black rounded-lg overflow-hidden mb-4 aspect-video">
                    <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
                    <div className="absolute bottom-4 inset-x-0 flex justify-center space-x-4">
                      <button type="button" onClick={capturePhoto} className="h-10 w-10 bg-white rounded-full border-4 border-gray-300" />
                      <button type="button" onClick={() => setShowCamera(false)} className="bg-red-600 text-white px-3 py-1 rounded-lg text-xs">Cancel</button>
                    </div>
                    <canvas ref={canvasRef} className="hidden" />
                  </div>
                )}

                <div className="flex flex-wrap gap-3">
                  {formData.images.map((img, idx) => (
                    <div key={idx} className="relative group h-20 w-20 bg-gray-100 rounded-lg overflow-hidden border">
                      <img src={img} alt="" className="w-full h-full object-cover" />
                      <button 
                        type="button" 
                        onClick={() => setFormData(p => ({ ...p, images: p.images.filter((_, i) => i !== idx) }))}
                        className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100"
                      >
                        <SafeIcon icon={FiX} className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  <div className="h-20 w-20 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-400">
                    <SafeIcon icon={FiImage} className="h-8 w-8" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Manufacturer *</label>
                  <input type="text" required value={formData.manufacturer} onChange={e => setFormData({...formData, manufacturer: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Model *</label>
                  <input type="text" required value={formData.model} onChange={e => setFormData({...formData, model: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
                <input type="text" required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Local SKU *</label>
                  <input type="text" required value={formData.localSku} onChange={e => setFormData({...formData, localSku: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Local Price *</label>
                  <input type="number" step="0.01" required value={formData.localPrice} onChange={e => setFormData({...formData, localPrice: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assign to Pallet</label>
                <select value={formData.palletId} onChange={e => setFormData({...formData, palletId: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                  <option value="">-- Select Pallet --</option>
                  {pallets.filter(p => p.is_active).map(p => <option key={p.id} value={p.id}>{p.pallet_number}</option>)}
                </select>
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t font-semibold">
                <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg">Cancel</button>
                <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center">
                  <SafeIcon icon={FiPlus} className="mr-2 h-4 w-4" /> Create Product
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AddProductModal;